import React from 'react';

import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { BookOfferResponseModel } from '../../../../../shared/ui-models/book.ui-model';
import { FeeType } from '../../../../../shared/enums/amount-type.enum';
import NumberFormat from 'react-number-format';
import ArrowForward from '@material-ui/icons/ArrowForward';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';

export interface PaymentDetailsProps {
  data: {
    unitType: UnitTypeUiModel;
    bookOffer: BookOfferResponseModel;
    checkInDate: Date;
    checkOutDate: Date;
    guestDetails?: {
      name: string;
      phoneNumber: string;
      email: string;
    };
  };
}

/**
 * functional component  PaymentDetails
 * @param { PaymentDetailsProps}
 */
export const PaymentDetails: React.FC<PaymentDetailsProps> = ({ data }) => {
  const currencyPrefix = (currency?: string) => {
    if (!currency) return 'EGP ';
    const key = currency.toLowerCase() as keyof typeof CURRENCY;
    return CURRENCY[key] ? CURRENCY[key] + ' ' : 'EGP ';
  };

  return (
    <div className="mb-12">
      <div className="rounded-xl border border-black/10 bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.06)] md:p-7">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 md:gap-6">
          <div className="min-w-0">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-black/60">
              Check in
            </div>
            <div className="text-xl text-black/90 md:text-2xl">
              {new Date(data.checkInDate).toDateString()}
            </div>
          </div>

          <div className="flex items-center justify-center text-black/55">
            <ArrowForward color="inherit" fontSize="large" />
          </div>

          <div className="min-w-0">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-black/60">
              Check out
            </div>
            <div className="text-xl text-black/90 md:text-2xl">
              {new Date(data.checkOutDate).toDateString()}
            </div>
          </div>
        </div>

        {data.bookOffer && data.bookOffer.oneTimePayment && (
          <div className="mt-5 border-t border-black/5 pt-5">
            <div className="mb-4 text-base font-bold text-black/85">
              {data.bookOffer.recurringPayment && data.bookOffer.recurringPayment.length > 0
                ? 'One Time Payment'
                : 'Payments'}
            </div>

            <div className="flex flex-col gap-3">
              {data.bookOffer.oneTimePayment.map((item, index) => (
                <div
                  key={`${item.feeName}-${item.feeType}-${item.feeValue}-${index}`}
                  className="grid grid-cols-[1fr_auto] items-baseline gap-4"
                >
                  <div className="font-normal text-black/80">
                    {item.feeName === 'Unit Price' && item.originalPrice && item.duration
                      ? item.originalPrice / item.duration
                      : item.feeName}{' '}
                    {item.feeType === FeeType.PERCENTAGE
                      ? `(${item.percentValue}%)`
                      : item.duration
                      ? item.mode === 'daily'
                        ? ` x ${item.duration} night(s)`
                        : item.mode === 'monthly'
                        ? ` x ${item.duration} month(s)`
                        : data.unitType?.reservationMode === 'daily'
                        ? ` x ${item.duration} night(s)`
                        : ` x ${item.duration} month(s)`
                      : ''}
                  </div>

                  <div className="inline-flex items-baseline gap-3 whitespace-nowrap">
                    {item.feeType !== FeeType.PERCENTAGE &&
                      item.originalPrice &&
                      item.originalPrice !== item.feeValue && (
                        <div className="font-semibold text-red-600/90 line-through">
                          <NumberFormat
                            value={item.originalPrice}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={
                              item.currency
                                ? currencyPrefix(item.currency)
                                : currencyPrefix(data.unitType?.currency)
                            }
                          />
                        </div>
                      )}

                    <div className="font-bold text-black/90">
                      <NumberFormat
                        value={item.feeValue}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={
                          item.currency
                            ? currencyPrefix(item.currency)
                            : currencyPrefix(data.unitType?.currency)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}

              {data.bookOffer.oneTimePayment.length ? (
                <div className="mt-1 grid grid-cols-[1fr_auto] items-baseline gap-4 border-t border-black/15 pt-4">
                  <div className="font-semibold text-black/90">Total</div>
                  <div className="whitespace-nowrap font-extrabold text-black/95">
                    <NumberFormat
                      value={data.bookOffer.oneTimePayment.reduce(
                        (prev, cur) => prev + Number(cur.feeValue),
                        0
                      )}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={
                        data.bookOffer.oneTimePayment.length > 0 &&
                        data.bookOffer.oneTimePayment[0].currency
                          ? currencyPrefix(data.bookOffer.oneTimePayment[0].currency)
                          : currencyPrefix(data.unitType?.currency)
                      }
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        )}

        {data.bookOffer && data.bookOffer.recurringPayment && (
          <div className="mt-5 border-t border-black/5 pt-5">
            <div className="mb-4 text-base font-bold text-black/85">Monthly Payment</div>

            <div className="flex flex-col gap-3">
              {data.bookOffer.recurringPayment.map((item, index) => (
                <div
                  key={`${item.feeName}-${item.feeType}-${item.feeValue}-${
                    item.startAtMonth ?? 'na'
                  }-${index}`}
                  className="grid grid-cols-[1fr_auto] items-baseline gap-4"
                >
                  <div className="font-normal text-black/80">
                    {item.feeName}{' '}
                    {item.feeType === FeeType.PERCENTAGE ? `(${item.percentValue}%)` : ''}
                    {item.startAtMonth ? `(starting from month ${item.startAtMonth})` : ''}
                  </div>

                  <div className="inline-flex items-baseline gap-3 whitespace-nowrap">
                    {item.feeType !== FeeType.PERCENTAGE &&
                      item.originalPrice &&
                      item.originalPrice !== item.feeValue && (
                        <div className="font-semibold text-red-600/90 line-through">
                          <NumberFormat
                            value={item.originalPrice}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={
                              item.currency
                                ? currencyPrefix(item.currency)
                                : currencyPrefix(data.unitType?.currency)
                            }
                          />
                        </div>
                      )}

                    <div className="font-bold text-black/90">
                      <NumberFormat
                        value={item.feeValue}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={
                          item.currency
                            ? currencyPrefix(item.currency)
                            : currencyPrefix(data.unitType?.currency)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}

              {data.bookOffer.recurringPayment.length ? (
                <div className="mt-1 grid grid-cols-[1fr_auto] items-baseline gap-4 border-t border-black/15 pt-4">
                  <div className="font-semibold text-black/90">Total</div>
                  <div className="whitespace-nowrap font-extrabold text-black/95">
                    <NumberFormat
                      value={data.bookOffer.recurringPayment.reduce(
                        (prev, cur) => prev + Number(cur.feeValue),
                        0
                      )}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={
                        data.bookOffer.recurringPayment.length > 0 &&
                        data.bookOffer.recurringPayment[0].currency
                          ? currencyPrefix(data.bookOffer.recurringPayment[0].currency)
                          : currencyPrefix(data.unitType?.currency)
                      }
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        )}

        {data.guestDetails && (
          <div className="mt-5 border-t border-black/5 pt-5">
            <div className="mb-4 text-base font-bold text-black/85">Guest</div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6">
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-black/60">
                  Name
                </div>
                <div className="text-xl text-black/90 md:text-2xl">{data.guestDetails.name}</div>
              </div>

              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-black/60">
                  Phone number
                </div>
                <div className="text-xl text-black/90 md:text-2xl">
                  {data.guestDetails.phoneNumber}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-black/60">
                  Email
                </div>
                <div className="text-xl text-black/90 md:text-2xl">{data.guestDetails.email}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
